/**
 * On-device dictation, as an outage fallback only.
 *
 * ─── WHAT THIS ACTUALLY IS ──────────────────────────────────────────────────
 * The Web Speech API hands recognition to whatever the platform provides:
 *   • iOS / macOS Safari → Siri's dictation engine
 *   • Chrome / Android   → Google's server-side recognition
 *   • Firefox            → nothing; the API is absent
 *
 * On iOS that means Siri, which Mick has said is frequently poor. So this is
 * NEVER the primary path and never silently substituted for Groq — it exists so
 * that a provider outage, or a deployment where the keys aren't set up yet,
 * degrades to something usable instead of a dead mic. Callers must tell the
 * user when it's in play.
 *
 * It also does NOT touch the quota: nothing is sent to our server, so there is
 * nothing to bill. That is why an exhausted free tier does NOT fall back here —
 * doing so would turn a hard limit into an unlimited free tier by another name
 * (decisions log: "not a fallback to Web Speech").
 */

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechResultEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechResultEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

function ctor(): SpeechRecognitionCtor | null {
  const w = globalThis as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition) as SpeechRecognitionCtor ?? null;
}

export function webSpeechAvailable(): boolean {
  return ctor() !== null;
}

export interface LocalDictation {
  stop(): void;
}

export interface LocalDictationOptions {
  /** Fired with each finalized phrase. Interim results are discarded. */
  onText(text: string): void;
  /** Fired once when recognition ends for a reason other than stop(). */
  onError(reason: string): void;
}

/**
 * Start on-device dictation. Returns null when unsupported.
 *
 * Only final results are surfaced: interim ones churn as the engine revises
 * itself, and inserting them would make the textarea flicker and rewrite text
 * under the caret.
 */
/**
 * How many times in a row the engine may end without producing anything before
 * we conclude it is not working and give up.
 *
 * Restarting on `onend` is REQUIRED (Safari ends continuous sessions
 * constantly), but restarting unconditionally is a trap: an engine that cannot
 * hear anything — mic still held by the recorder we just released, permission
 * granted but not yet in effect, no audio route — ends instantly every time.
 * The wrapper then respawns it forever, the mic indicator flashes, and no text
 * ever appears. That reached a real iPhone on 2026-09-17.
 *
 * A session that produces text resets the counter, so ordinary dictation can
 * restart as often as the platform likes.
 */
const MAX_BARREN_RESTARTS = 4;

export function startLocalDictation(opts: LocalDictationOptions): LocalDictation | null {
  const Ctor = ctor();
  if (!Ctor) return null;

  let stopped = false;
  let rec: SpeechRecognitionLike | null = null;
  /** Consecutive sessions that ended having produced no text. */
  let barrenRestarts = 0;
  /** Whether the CURRENT session has produced anything. */
  let producedThisSession = false;

  const begin = (): void => {
    if (stopped) return;
    rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-US';
    producedThisSession = false;

    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]!;
        if (!r.isFinal) continue;
        const text = r[0]?.transcript?.trim();
        if (text) {
          producedThisSession = true;
          barrenRestarts = 0;
          opts.onText(text);
        }
      }
    };

    rec.onerror = (e) => {
      const reason = e.error ?? 'unknown';
      // 'no-speech' and 'aborted' are routine; restarting is the right answer.
      if (reason === 'no-speech' || reason === 'aborted') return;
      stopped = true;
      opts.onError(reason);
    };

    rec.onend = () => {
      // Safari ends the session on its own every few seconds even in
      // continuous mode. Restart unless we asked it to stop, or dictation
      // would silently die mid-sentence.
      if (stopped) return;

      if (!producedThisSession) {
        barrenRestarts += 1;
        if (barrenRestarts > MAX_BARREN_RESTARTS) {
          // The engine is running but hearing nothing. Say so and stop, rather
          // than respawning behind a mic light that never yields text.
          stopped = true;
          opts.onError('no-audio');
          return;
        }
      }
      begin();
    };

    try {
      rec.start();
    } catch {
      stopped = true;
      opts.onError('start-failed');
    }
  };

  begin();

  return {
    stop(): void {
      stopped = true;
      try { rec?.stop(); } catch { /* already ended */ }
      rec = null;
    },
  };
}
